import { useCallback, useContext, useMemo } from 'react'

import { useSelector } from 'react-redux'
import { isUndefined } from 'lodash'

import DragTable, { DEFAULT_SELECTED_VALUES as DRAG_TABLE_DEFAULT_SELECTED_VALUES } from '../../../../Components/antd/Tables/DragTable/DragTable'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import { firstOrDefault, getIdOrNull, isNonEmptyArray, isObject, isOptimistic, isPositive } from '../../../../utils'
import { areEquivalent, Ownership, isContextDraggable } from '../../../../utils/entities/internship'
import { getDivStyle, getInternshipsMatrix, getInternshipsPeriods, internshipEvent } from '../../../../Components/shared/Internship/utils'
import { selectUser } from '../../../../reducers/UserReducer'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { DATE_FORMAT_API } from '../../../../utils/constants'
import { Actions, dataUpsert, deleteData, Events } from '../../../../utils/events'
import { ColorsPalette } from '../../../../config/colors'
import { colorShade, getLuminanceFromColorCode } from '../../../../utils/colors'
import { getEndOf, getStartOf, ISO_WEEK } from '../../../../utils/momentjs'
import InternshipOccupancyDisplayer from './InternshipOccupancyDisplayer'
import { InternshipFormModalContext, Types } from '../../../../Providers/Forms/InternshipForm/InternshipFormModal'
import useInternship from '../../../../utils/api/hooks/useInternship'
import SectionTooltip from '../../Quotas/SchoolQuotasView/SectionTooltip'
import { Axes, Types as Background } from '../../../../Components/antd/Tables/DragTable/ResizingDiv'
import { isInstitutionUser } from '../../../../utils/roles'
import { onError } from '../../../../utils/apiHelper'
import { getKeyConfig, getLaneConfig, getLaneKey, getQuotasMatrix, INCLUSION_LINE_KEY, LaneTypes, sortLanes } from '../../../../Components/shared/Quotas/utils'
import SchoolQuotaDisplayer from '../../Quotas/SchoolQuotasView/SchoolQuotaDisplayer'
import HighlightedColumnsProvider from '../../../../Providers/Quotas/HighlightedColumnsProvider'
import { includeSection } from '../../../../utils/entities/sectionGroup'
import LOCAL_STORAGE_KEYS from '../../../../utils/localStorage'

export const DEFAULT_SELECTED_VALUES = { ...DRAG_TABLE_DEFAULT_SELECTED_VALUES, startDate: null }

export const DisplayTypes = Object.freeze({
  SCHOOL: 'SCHOOL',
  SECTION: 'SECTION'
})

const DEFAULT_COLOR = {
  border: ColorsPalette.GREY,
  background: colorShade(ColorsPalette.FOREST_GREEN, 30),
  text: getLuminanceFromColorCode(ColorsPalette.FOREST_GREEN)
}

const { SECTION, GROUP } = LaneTypes

const PlacesOccupancyTable = ({
  config, holidayPeriods, internships, period, quotasMap, school, sector, seed, selectedData, studentPathways, sectionGroups, type = DisplayTypes.SCHOOL,
  onSelect, onChange
}) => {
  const { setFormHandler } = useContext(InternshipFormModalContext)
  const { coloredSchools, coloredSectionToYears, sectionsColorMap } = useContext(InstitutionContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const internshipsHandler = useInternship({ callback: onChange })

  const periods = useMemo(() => getInternshipsPeriods(period, t), [t, period])
  const colorsMap = useMemo(() => (type === DisplayTypes.SCHOOL ? coloredSchools : coloredSectionToYears).reduce((acc, entity) => {
    acc[entity.id] = entity.color ?? DEFAULT_COLOR

    return acc
  }, {}), [coloredSchools, coloredSectionToYears, type])
  const seedDate = useMemo(() => periods.weeks[0], [periods.weeks])

  const lines = useMemo(() => {
    const inclusionLine = { id: INCLUSION_LINE_KEY, name: '*' }

    const linesConfig = quotasMap.places.reduce((acc, place) => {
      const { type, id, name } = getLaneConfig(place)
      const key = id === INCLUSION_LINE_KEY ? id : getLaneKey(type, id)

      if (!acc[key]) {
        acc[key] = { id: key, places: 0, name, allowedYears: [], internships: [], infos: [] }
      }

      acc[key].places += place.total

      if (key !== INCLUSION_LINE_KEY) {
        acc[INCLUSION_LINE_KEY].places -= place.total
      }

      return acc
    }, { [inclusionLine.id]: { id: inclusionLine.id, places: 0, name: inclusionLine.name, allowedYears: [], internships: [], infos: [] } })

    if (studentPathways.length) {
      studentPathways.forEach(sp => {
        const section = sp.sectionToYear.section.id
        const group = sectionGroups.find(group => includeSection(group, section))

        const laneKey = !isUndefined(group)
          ? getLaneKey(GROUP, group)
          : getLaneKey(SECTION, section)

        const key = linesConfig[laneKey] ? laneKey : INCLUSION_LINE_KEY

        linesConfig[key].allowedYears.push(sp.sectionToYear)
      })
    }

    let remainingInternships = [...internships]

    Object.keys(linesConfig).forEach(key => {
      const { type } = getKeyConfig(key)
      const { allowedYears, places } = linesConfig[key]

      linesConfig[key].tooltip = <SectionTooltip sectionToYears={allowedYears} places={places} type={type} />

      const processedInternships = remainingInternships.reduce((acc, i) => {
        if (parseInt(key) === INCLUSION_LINE_KEY && linesConfig[key].places === 0) {
          acc.extracted.push({ ...i, isException: true })
        } else if (linesConfig[key].allowedYears.some(year => getIdOrNull(year) === getIdOrNull(i.section?.sectionToYear))) {
          acc.extracted.push({ ...i, isException: false })
        } else if (getIdOrNull(i.section?.sectionToYear?.section) === parseInt(key)) {
          acc.extracted.push({ ...i, isException: linesConfig[key].allowedYears.length > 0 })
        } else if (linesConfig[inclusionLine.id].allowedYears.length === 0) {
          acc.extracted.push({ ...i, isException: false })
        } else {
          acc.remaining.push(i)
        }

        return acc
      }, { extracted: [], remaining: [] })

      linesConfig[key].internships = processedInternships.extracted
      remainingInternships = processedInternships.remaining
    })

    if (config.overlay.displaySectionsExceptions) {
      linesConfig[inclusionLine.id].internships = linesConfig[inclusionLine.id].internships.concat(remainingInternships.map(i => ({ ...i, isException: true })))
    }

    const lanes = Object.values(linesConfig)
    const inclusions = lanes.shift()

    return [inclusions].concat(sortLanes(lanes))
  }, [internships, quotasMap, sectionGroups, studentPathways, config.overlay.displaySectionsExceptions])

  const internshipConfig = useCallback(internship => {
    const disabled = !isContextDraggable(user, internship)

    const colorKey = type === DisplayTypes.SCHOOL ? internship.school : internship.internshipYear

    return {
      style: getDivStyle(colorsMap[getIdOrNull(colorKey)] ?? DEFAULT_COLOR),
      disabled: disabled,
      draggableAxes: disabled ? [Axes.VERTICAL] : Object.values(Axes),
      resizableAxes: disabled ? [] : [Axes.HORIZONTAL]
    }
  }, [user, colorsMap, type])

  const quotasData = useMemo(() => getQuotasMatrix(
    lines,
    periods.weeks,
    quotasMap.quotas,
    quota => ({
      style: getDivStyle((type === DisplayTypes.SCHOOL ? colorsMap[getIdOrNull(quota.school)] : sectionsColorMap[getIdOrNull(quota.sectionToYear)]) ?? DEFAULT_COLOR),
      disabled: true,
      draggableAxes: [],
      resizableAxes: [],
      readonly: true,
      type: Background.TRANSLUCID
    }),
    quota => <SchoolQuotaDisplayer quota={quota} overlay={config.overlay} isDesiderata={false} />
  ), [quotasMap.quotas, periods.weeks, lines, colorsMap, config.overlay])

  const internshipsData = useMemo(() => {
    const internshipMatrix = getInternshipsMatrix(
      lines,
      periods.weeks,
      sectionGroups,
      internship => internshipConfig(internship),
      internship => <InternshipOccupancyDisplayer internship={internship} overlay={config.overlay} isSectionException={internship.isException} onChange={onChange} />,
      LOCAL_STORAGE_KEYS.PLACES_OCCUPANCY_VIEW.INTERNSHIPS_MATRIX
    )

    if (config.overlay.displayQuotas) {
      Object.keys(internshipMatrix.data).forEach(sectorId => {
        internshipMatrix.data[sectorId] = internshipMatrix.data[sectorId].concat(quotasData.data[sectorId])
        internshipMatrix.overflows[sectorId] = Math.max(internshipMatrix.overflows[sectorId], quotasData.overflows[sectorId])
      })
    }

    return internshipMatrix
  }, [internships, config.overlay.displayQuotas, periods.weeks, lines, quotasData.data, sectionGroups])

  const holidayWeeks = useMemo(() => {
    if (holidayPeriods.length === 0) return []

    const holidayWeeksIndexes = periods.weeks.reduce((acc, week, index) => {
      if (holidayPeriods.some(holiday => holiday.includes(week))) {
        acc.push(index)
      }

      return acc
    }, [])

    return holidayWeeksIndexes
  }, [holidayPeriods, periods.weeks])

  const handleUpsert = useCallback(internship => dataUpsert(internship, firstOrDefault(selectedData.values), internshipsHandler.upsert, areEquivalent), [selectedData.values, internshipsHandler.upsert])

  const handleAction = useCallback((event, internship, numberOfInternships = 1) => {
    if (isOptimistic(internship)) {
      internship.ownership = isInstitutionUser(user) ? Ownership.INSTITUTION : Ownership.SCHOOL

      if (isObject(school)) {
        internship.school = school
      }
    }

    if (event === Events.DRAG_STOP) {
      handleUpsert(internship)
    } else if ([Events.DOUBLE_CLICK, Events.LONG_CLICK].includes(event)) {
      if (isOptimistic(internship.bareSection) && isOptimistic(internship) && [internship.school, internship.sector].every(getIdOrNull)) {
        handleUpsert(internship)
      } else {
        setFormHandler({ data: internship, quantity: numberOfInternships, callback: onChange, type: numberOfInternships === 1 ? Types.SINGLE : Types.BULK })
      }
    } else if (event === Events.DELETE_PRESSED) {
      deleteData(selectedData.values, internshipsHandler.delete)
    }
  }, [school, selectedData.values, onChange, deleteData, handleUpsert, internshipsHandler.create, internshipsHandler.delete])

  const handleEvent = useCallback((rawEvent, data, key) => {
    let numberOfInternships = 1

    const { internship, event } = internshipEvent(
      internshipsData.data[key] ?? [],
      internshipsData.matrix[key] ?? [],
      LOCAL_STORAGE_KEYS.PLACES_OCCUPANCY_VIEW.INTERNSHIPS_MATRIX,
      rawEvent,
      seed,
      data
    )

    if (event !== Events.STRICT_VERTICAL_DRAG_STOP) {
      if (internship !== null && (event !== Events.CLICK || data.data !== null)) {
        const startDate = getStartOf(seedDate, ISO_WEEK, data.x[0]).format(DATE_FORMAT_API)
        const endDate = getEndOf(seedDate, ISO_WEEK, data.x[1]).format(DATE_FORMAT_API)

        internship.startDate = startDate
        internship.endDate = endDate
        internship.total = 1

        numberOfInternships += data.y[1] - data.y[0]
      }

      const isValidKey = !isUndefined(key) && key !== INCLUSION_LINE_KEY
      if (isValidKey && internship !== null) {
        const { type, id } = getKeyConfig(key)

        if (type === SECTION) {
          internship.bareSection = { id }
        }
      }

      handleAction(event, internship, numberOfInternships)
    } else {
      onChange(internship, Actions.RELOAD)
    }
  }, [seed, periods, seedDate, handleAction])

  const displayIllegalDragMessage = useCallback(() => onError(t('places_occupancy_table.illegal_drag.message')), [t])

  const handleSelect = useCallback(({ values, selectedCell }) => {
    const newSelected = { values }

    if (isObject(selectedCell)) {
      newSelected.startDate = getStartOf(seedDate, ISO_WEEK, selectedCell.x[0]).format(DATE_FORMAT_API)
    } else if (selectedCell === null) {
      newSelected.startDate = null
    }

    onSelect(selected => ({ ...selected, ...newSelected }))
  }, [onSelect, seedDate])

  const swimlanes = useMemo(() => {
    const lanes = [...lines]
    const inclusions = lanes.shift()

    const willContainInternships = isNonEmptyArray(internshipsData.data[INCLUSION_LINE_KEY]) || isNonEmptyArray(internshipsData.overflows[INCLUSION_LINE_KEY])
    const willContainQuotas = isNonEmptyArray(quotasData.data[INCLUSION_LINE_KEY]) || isNonEmptyArray(quotasData.overflows[INCLUSION_LINE_KEY])

    const initialLane = isPositive(inclusions.places, true) || (willContainInternships || willContainQuotas) ? [inclusions] : []

    return initialLane.concat(lanes)
  }, [internshipsData, lines])

  const firstLaneColor = useMemo(() => swimlanes.find(l => l.id === INCLUSION_LINE_KEY)?.places === 0 ? ColorsPalette.GREY : undefined, [swimlanes])

  return (
    <HighlightedColumnsProvider>
      <DragTable
        firstLaneColor={firstLaneColor}
        columns={periods.months}
        config={config}
        data={internshipsData.data}
        disabled={isObject(sector)}
        highlightedColumns={holidayWeeks}
        lines={swimlanes}
        overflows={internshipsData.overflows}
        selectedData={selectedData.values}
        title={t('common.sections')}
        onEvent={handleEvent}
        onIllegalDrag={displayIllegalDragMessage}
        onSelectData={handleSelect}
      />
    </HighlightedColumnsProvider>
  )
}

export default PlacesOccupancyTable
